import t from '@jetshop/intl';
import Accordion from '@jetshop/ui/Accordion/Accordion';
import ProductSpecifications from '@jetshop/ui/ProductSpecifications/ProductSpecifications';
import React, { Fragment, useState } from 'react';
import { styled } from 'linaria/react';
import {
  ProductSection as UIProductSection,
  SectionHeading
} from './ProductInfo';
import { ReactComponent as Caret } from '../../svg/Caret.svg';
import { cx } from 'linaria';
import { theme } from '../Theme';
import { Link } from 'react-router-dom';
import { PriceHistoryWrapper } from './ProductHistory';

const StyledCaret = styled(Caret)`
  align-self: center;
  color: #787878;
  transition: all 0.2s ease-in-out;

  &.open {
    transform: rotate(-90deg);
  }
  &.closed {
    transform: rotate(0deg);
  }
`;

const Description = styled('article')`
  line-height: 20px;
  span {
    display: contents;
  }
  li {
    list-style-type: disc;
    list-style-position: inside;
  }
`;

const ProductSection = styled(UIProductSection)`
  cursor: pointer;
  border-bottom: 1px solid #e8e8e8;
  padding: 1rem 0;
  margin: 0;
  font-size: 14px;
  width: 100%;

  ${theme.below.lg} {
    font-size: 12px;
  }

  ${SectionHeading} {
    display: flex;
    justify-items: space-between;
    align-items: center;
    margin-bottom: 0;
    font-size: 20px;
    font-weight: 400;
    font-family: ${theme.fonts.primary};
    letter-spacing: 0.05em;
    + * > :first-child {
      margin-top: 1rem;
    }
  }
`;

const IndicatorIcon = styled('span')`
  margin-left: auto;
  font-size: 1.25rem;
`;

const ReadMoreButton = styled('button')`
  background: none;
  border: none;
  color: #000;
  text-decoration: underline;
  cursor: pointer;
  font-size: 14px;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const AccordionIndicator = ({ isOpen }) => (
  <IndicatorIcon>
    {<StyledCaret className={cx(!isOpen ? 'open' : 'closed')} />}
  </IndicatorIcon>
);

const ProductInfoAccordion = ({
  product,
  brand,
  brandContent,
  selectedVariation
}) => {
  //Adds vareID to product specifications
  const vareID = {
    key: 'vareID',
    type: 'STRING',
    title: 'VareID',
    stringValue: product?.articleNumber,
    __typename: 'CustomStringField'
  };

  const specifications = [vareID];
  const [showFullDescription, setShowFullDescription] = useState(false);
  const shortDescription = product?.description?.substring(0, 600) + '...';

  const extendedSpecifications = () => {
    const excludedKeys = ['variants', 'STL_image', 'STL_products'];

    product.customFields.forEach(field => {
      if (!excludedKeys.includes(field.key)) {
        specifications.push(field);
      }
    });
    return specifications;
  };
  extendedSpecifications();

  return (
    <Accordion single initialOpenIndexes={[0]}>
      {({ openIndexes, handleClick, AccordionContent }) => (
        <Fragment>
          <ProductSection>
            <SectionHeading onClick={() => handleClick(0)}>
              <span>{t('Product description')}</span>
              <AccordionIndicator isOpen={openIndexes.includes(0)} />
            </SectionHeading>
            <AccordionContent isOpen={openIndexes.includes(0)}>
              {showFullDescription ? (
                <Description
                  dangerouslySetInnerHTML={{
                    __html: product.description
                  }}
                />
              ) : (
                <Description
                  dangerouslySetInnerHTML={{
                    __html: shortDescription
                  }}
                />
              )}

              <ReadMoreButton
                onClick={() => setShowFullDescription(!showFullDescription)}
              >
                {showFullDescription ? 'Les mindre' : 'Les mer'}
              </ReadMoreButton>
            </AccordionContent>
          </ProductSection>
          <ProductSection>
            <SectionHeading onClick={() => handleClick(1)}>
              <span>{t('Specifications')}</span>
              <AccordionIndicator isOpen={openIndexes.includes(1)} />
            </SectionHeading>
            <AccordionContent isOpen={openIndexes.includes(1)}>
              <ProductSpecifications fields={specifications} />
            </AccordionContent>
          </ProductSection>
          {brand && brandContent && (
            <ProductSection>
              <SectionHeading onClick={() => handleClick(2)}>
                <span>Om {brand?.name}</span>
                <AccordionIndicator isOpen={openIndexes.includes(2)} />
              </SectionHeading>
              <AccordionContent isOpen={openIndexes.includes(2)}>
                <div
                  dangerouslySetInnerHTML={{
                    __html: brandContent
                  }}
                />
              </AccordionContent>
            </ProductSection>
          )}
          <ProductSection>
            <SectionHeading onClick={() => handleClick(3)}>
              <span>{t('Shipping')}</span>
              <AccordionIndicator isOpen={openIndexes.includes(3)} />
            </SectionHeading>
            <AccordionContent isOpen={openIndexes.includes(3)}>
              <Description>
                Fri frakt på alle ordre over 999,- Som medlem i vårt
                lojalitetsprogram, får du fri frakt over 199,-
                <br />
                <br />
                Alle ordre blir som utgangspunkt plukket og sendt samme med en
                estimert levering på 1-3 dager.
                <br />
                <br />
                Det er muligheter for ekspressleveringen med Posten/Bring og
                Postnord for å sikre din levering raskt. For mange områder, så
                vil også Porterbuddy, kunne levere samme dag, på ordre mottatt
                innen 10:00.
                <br />
                <br />
                Merk at fri frakt ikke gjelder for Porterbuddy.
                <br />
                <br />
                Vi gir dere som kunder hos oss fri retur på varer, som ikke
                skulle passe eller ikke ble helt slik du hadde tenkt. Link til
                returskjema finner du på din ordrebekreftelse.
                <br />
                <br />
                Har du spørsmål vedr. frakt og retur, se våre{' '}
                <Link to={'/kjopsvilkar'}>kjøp- og leveringsvilkår</Link> eller
                send oss en mail på mail@ferner.no eller gå inn på vår chat.
              </Description>
            </AccordionContent>
          </ProductSection>
          <ProductSection>
            <SectionHeading onClick={() => handleClick(4)}>
              <span>{t('Price History')}</span>
              <AccordionIndicator isOpen={openIndexes.includes(4)} />
            </SectionHeading>
            <AccordionContent isOpen={openIndexes.includes(4)}>
              <PriceHistoryWrapper
                articleNumber={product.articleNumber}
                variant={selectedVariation}
              />
            </AccordionContent>
          </ProductSection>
        </Fragment>
      )}
    </Accordion>
  );
};

export default ProductInfoAccordion;
